/**
 * Typed HTTP client for the platform API.
 *
 * The part worth reading is `parseError`. The backend answers a plan boundary
 * with 402 and a structured payload — feature, current plan, required plan —
 * precisely so the UI can offer an upgrade instead of showing "something went
 * wrong". Flattening that into a generic Error here would throw away the only
 * reason the backend bothered.
 */

import type { paths } from './generated/schema';

export type ApiPaths = paths;

export interface ApiErrorBody {
  code: string;
  message: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.body = body;
  }
}

/** 401. The caller should send the user back to sign-in. */
export class UnauthenticatedError extends ApiError {
  override readonly name = 'UnauthenticatedError';
}

/** 403. The user is signed in; their role does not allow this. */
export class PermissionDeniedError extends ApiError {
  override readonly name = 'PermissionDeniedError';
  readonly permission: string;

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.permission = String(body.permission ?? '');
  }
}

/**
 * 402. The user is allowed; the plan is not.
 *
 * Distinct from PermissionDeniedError because the remedy is different: one is a
 * conversation with your manager, the other is a billing page.
 */
export class UpgradeRequiredError extends ApiError {
  override readonly name = 'UpgradeRequiredError';
  readonly feature: string | undefined;
  readonly limit: string | undefined;
  readonly cap: number | undefined;
  readonly currentPlan: string;
  readonly requiredPlan: string | undefined;
  readonly upgradeUrl: string;

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.feature = typeof body.feature === 'string' ? body.feature : undefined;
    this.limit = typeof body.limit === 'string' ? body.limit : undefined;
    this.cap = typeof body.cap === 'number' ? body.cap : undefined;
    this.currentPlan = String(body.current_plan ?? 'unknown');
    this.requiredPlan = typeof body.required_plan === 'string' ? body.required_plan : undefined;
    this.upgradeUrl = String(body.upgrade_url ?? '/billing/upgrade');
  }
}

/** What a sign-in is worth keeping: the pass, the means to renew it, and when. */
export interface SessionTokens {
  access: string;
  /**
   * Spent on the next renewal and replaced by a new one. Null for a session
   * stored before refresh existed — such a session still works until its access
   * token runs out, and then asks for a password like it always did.
   */
  refresh: string | null;
  /** Epoch ms at which the access token stops being accepted. */
  expiresAt: number;
}

export interface TokenStore {
  get(): SessionTokens | null;
  set(tokens: SessionTokens | null): void;
}

/** The server's answer to a sign-in or a renewal. */
export interface TokenPayload {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  tenant_id: string;
  refresh_token?: string;
  refresh_expires_in?: number;
}

export function tokensFrom(payload: TokenPayload): SessionTokens {
  return {
    access: payload.access_token,
    refresh: payload.refresh_token ?? null,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

/**
 * Token storage.
 *
 * localStorage is a deliberate, documented trade-off for Phase 0: it survives a
 * reload, which the POS needs, and it is readable by any script that gets onto
 * the page. The real fix is a httpOnly refresh cookie with an in-memory access
 * token; what rotation buys in the meantime is that a copied refresh token
 * stops working the moment the real device renews.
 */
export function createTokenStore(
  key = 'restaurant.session',
  /**
   * `session` keeps it to one browser tab and ends it with the tab: for a
   * session that must not spill into the other tabs of the same app — the
   * platform's support inside a back office an owner may have open beside it.
   */
  where: 'local' | 'session' = 'local',
): TokenStore {
  let memory: SessionTokens | null = null;
  let read = false;
  const storage = () => (where === 'session' ? window.sessionStorage : window.localStorage);

  return {
    get() {
      if (read) return memory;
      read = true;
      try {
        memory = parseStored(storage().getItem(key));
      } catch {
        memory = null;
      }
      return memory;
    },
    set(tokens) {
      memory = tokens;
      read = true;
      try {
        if (tokens === null) storage().removeItem(key);
        else storage().setItem(key, JSON.stringify(tokens));
      } catch {
        /* private mode, or storage disabled — the in-memory copy still works */
      }
    },
  };
}

function parseStored(raw: string | null): SessionTokens | null {
  if (raw === null) return null;
  // A session stored by a build that kept the bare access token. Honoured
  // rather than discarded: signing every till out on the deploy that adds
  // staying signed in would be a strange way to introduce it.
  if (!raw.startsWith('{')) return { access: raw, refresh: null, expiresAt: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<SessionTokens>;
    if (typeof parsed.access !== 'string') return null;
    return {
      access: parsed.access,
      refresh: typeof parsed.refresh === 'string' ? parsed.refresh : null,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0,
    };
  } catch {
    return null;
  }
}

export interface ClientOptions {
  baseUrl: string;
  tokens: TokenStore;
  /** Called when the session is gone for good, so the app can route to sign-in. */
  onUnauthenticated?: () => void;
}

/**
 * How close to the end of an access token's life to renew it.
 *
 * Early rather than on a 401, because a request that has already been
 * authorised is unaffected by a renewal landing behind it, whereas a 401 in the
 * middle of taking a payment has to be unpicked and sent again.
 */
const RENEW_WITHIN_MS = 60_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * The body as an object. Serialised here, so it must not arrive already
   * stringified — a string is valid JSON and gets encoded a second time, and
   * the server sees a JSON string where a body should be. That produced a 422
   * on every price-book entry and every role permission save, from code that
   * read perfectly. `object` rather than `unknown` so the compiler says so.
   */
  body?: object | undefined;
  signal?: AbortSignal;
  /**
   * Retry-safe key for writes. The POS runs on tablets with unreliable Wi-Fi;
   * the backend deduplicates payments, refunds, opened tabs and added lines by
   * this key, so asking again cannot double any of them. Hold one key per
   * attempt (`useAttemptKeys`, `usePaymentAttempt`), never one per call: a key
   * minted per call makes a double tap two writes.
   */
  idempotencyKey?: string;
  /**
   * Whether a 401 here means the session is over. True everywhere but one
   * place: changing your own password answers a wrong *current* password with
   * 401, and that is an answer about what was typed, not about the session that
   * asked. Renewing and signing out over it throws somebody out for a typo.
   */
  unauthenticatedEndsSession?: boolean;
}

/**
 * The client, and the renewal rules it has to keep.
 *
 * An access token lasts fifteen minutes, which was signing people out
 * mid-service: a manager counting stock for twenty minutes came back to a
 * password prompt and a lost count. A refresh token now renews it silently.
 *
 * **Every exchange spends the refresh token and returns a new one.** Sending a
 * spent one again is indistinguishable from a stolen copy and is treated as
 * one: the whole sign-in is revoked and the device goes back to a password. Two
 * rules follow, and they are structural here rather than incidental —
 *
 * 1. *One exchange in flight, ever.* Three requests expiring at once would
 *    otherwise start three renewals, and the second and third are replays.
 *    Later callers await the first one's promise.
 * 2. *The new token is written before anything else touches the response.*
 *
 * A retry after a renewal reuses the original `RequestOptions`, so a payment's
 * `Idempotency-Key` goes back up with it and the server returns the payment it
 * already took rather than taking it twice.
 */
export class ApiClient {
  private readonly baseUrl: string;
  readonly tokens: TokenStore;
  private readonly onUnauthenticated: (() => void) | undefined;
  private renewal: Promise<SessionTokens | null> | null = null;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.tokens = options.tokens;
    this.onUnauthenticated = options.onUnauthenticated;
  }

  /**
   * The access token to use right now, renewed first if it is nearly out.
   *
   * Public because the kitchen's socket carries the token in its URL, and a
   * wall screen can sit for hours without making a single HTTP request.
   */
  async accessToken(): Promise<string | null> {
    return (await this.current())?.access ?? null;
  }

  /**
   * A file, with the session's token, as bytes and the name the server gave it.
   *
   * For exports. A plain link to an export cannot carry the bearer token, so
   * every "Download" on a report used to open a page reading "Sign in to
   * continue". Renews first like any request; an error comes back as JSON and
   * is thrown as the same `ApiError` the rest of the client throws.
   */
  async download(
    path: string,
    body?: unknown,
  ): Promise<{ blob: Blob; filename: string | null }> {
    const token = await this.accessToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    // A body makes it a POST: a built report is described by more than fits
    // comfortably in a query string.
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let response: Response;
    try {
      response = await fetch(
        `${this.baseUrl}${path}`,
        body === undefined
          ? { headers }
          : { method: 'POST', headers, body: JSON.stringify(body) },
      );
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_unreachable',
        message: 'Cannot reach the server. Check your connection.',
        cause: String(cause),
      });
    }
    if (!response.ok) throw parseError(response.status, await readJson(response));
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const named = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: await response.blob(), filename: named?.[1] ?? null };
  }

  /**
   * Send a file as multipart form data, with the session's token.
   *
   * Separate from `request`, which always sends JSON: the browser has to set
   * the multipart boundary itself, so no Content-Type is given here. Renews
   * first, like any request; an error is thrown as the same `ApiError`.
   */
  async upload<T>(path: string, form: FormData): Promise<T> {
    const token = await this.accessToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, { method: 'POST', headers, body: form });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_unreachable',
        message: 'Cannot reach the server. Check your connection.',
        cause: String(cause),
      });
    }
    const payload = await readJson(response);
    if (!response.ok) throw parseError(response.status, payload);
    return payload as T;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const session = await this.current();
    return this.send<T>(path, options, session?.access ?? null, true);
  }

  private async send<T>(
    path: string,
    options: RequestOptions,
    token: string | null,
    mayRenew: boolean,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method: options.method ?? 'GET',
        headers,
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (cause) {
      // A network failure is not a server error, and the difference matters to
      // the POS: one means retry when you reconnect, the other means stop.
      throw new ApiError(0, {
        code: 'network_unreachable',
        message: 'Cannot reach the server. Check your connection.',
        cause: String(cause),
      });
    }

    if (response.status === 204) return undefined as T;

    const payload = await readJson(response);

    if (!response.ok) {
      const error = parseError(response.status, payload);
      if (
        error instanceof UnauthenticatedError &&
        options.unauthenticatedEndsSession !== false
      ) {
        // One renewal, one retry, and the same options both times — so a write
        // goes back up under the key it had on the first attempt.
        if (mayRenew) {
          const renewed = await this.renew(token);
          if (renewed !== null) return this.send<T>(path, options, renewed.access, false);
        } else {
          this.end();
        }
      }
      throw error;
    }

    return payload as T;
  }

  /**
   * End the session at this device's end, and at the server's.
   *
   * The tokens go first: signing out must not wait on a network that may be
   * the reason somebody is signing out. The server call revokes the whole
   * family — every device descended from that sign-in — and answers 204
   * whatever it is given, so there is nothing here to handle.
   */
  async signOut(): Promise<void> {
    const session = this.tokens.get();
    this.tokens.set(null);
    if (session?.refresh == null) return;
    try {
      await fetch(`${this.baseUrl}/v1/auth/logout`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: session.refresh }),
      });
    } catch {
      // It expires on its own in thirty days. Nothing to tell anybody.
    }
  }

  /** The session, renewed if the access token is nearly out. */
  private async current(): Promise<SessionTokens | null> {
    const session = this.tokens.get();
    if (session === null || session.refresh === null) return session;
    if (Date.now() < session.expiresAt - RENEW_WITHIN_MS) return session;
    return this.renew(session.access);
  }

  private renew(spentWith: string | null): Promise<SessionTokens | null> {
    const session = this.tokens.get();
    if (session === null || session.refresh === null) {
      this.end();
      return Promise.resolve(null);
    }
    // Somebody renewed while this caller was in flight, and its replacement is
    // already in the store. Spending the old refresh token again is the replay
    // the server revokes a sign-in over.
    if (spentWith !== null && session.access !== spentWith) return Promise.resolve(session);

    this.renewal ??= this.exchange(session.refresh).finally(() => {
      this.renewal = null;
    });
    return this.renewal;
  }

  private async exchange(refresh: string): Promise<SessionTokens | null> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
    } catch {
      // The network is down, not the session. Keep what we have; whatever
      // wanted this will fail with its own network error and can be sent again
      // when the Wi-Fi comes back.
      return this.tokens.get();
    }

    if (!response.ok) {
      this.end();
      return null;
    }

    const payload = (await readJson(response)) as TokenPayload;
    // Written before anything else reads the response: a renewal that returns
    // a token and then loses it has spent the old one for nothing.
    const next = tokensFrom(payload);
    this.tokens.set(next);
    return next;
  }

  private end(): void {
    this.tokens.set(null);
    this.onUnauthenticated?.();
  }

  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, signal ? { signal } : {});
  }

  post<T>(path: string, body?: object, options: Omit<RequestOptions, 'method' | 'body'> = {}) {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { code: 'invalid_response', message: text.slice(0, 200) };
  }
}

function parseError(status: number, payload: unknown): ApiError {
  const body = normalizeBody(status, payload);
  switch (status) {
    case 401:
      return new UnauthenticatedError(status, body);
    case 402:
      return new UpgradeRequiredError(status, body);
    case 403:
      return new PermissionDeniedError(status, body);
    default:
      return new ApiError(status, body);
  }
}

function normalizeBody(status: number, payload: unknown): ApiErrorBody {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    if (typeof record.code === 'string' && typeof record.message === 'string') {
      return record as ApiErrorBody;
    }
    // FastAPI's own validation errors do not use our envelope. Translate rather
    // than surfacing a raw pydantic array to a waiter mid-service.
    if (Array.isArray(record.detail)) {
      return {
        code: 'invalid_request',
        message: firstValidationMessage(record.detail),
        detail: record.detail,
      };
    }
    if (typeof record.detail === 'string') {
      return { code: 'error', message: record.detail };
    }
  }
  return { code: 'error', message: `Request failed (${status})` };
}

function firstValidationMessage(detail: unknown[]): string {
  const first = detail[0];
  if (first && typeof first === 'object') {
    const entry = first as { loc?: unknown[]; msg?: unknown };
    const field = Array.isArray(entry.loc) ? entry.loc[entry.loc.length - 1] : undefined;
    const message = typeof entry.msg === 'string' ? entry.msg : 'is invalid';
    return field ? `${String(field)}: ${message}` : message;
  }
  return 'The request was rejected';
}
